//single panel dashboard wrking fine

import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
    FaTimes, FaCheckCircle, FaShoppingBag,
    FaPlay, FaShoppingCart, FaUserCircle,
    FaCalendarAlt, FaClock, FaPlus
} from "react-icons/fa";
import { isTestUnlocked, hasActiveSubscription, isSubscriptionExpired, setPlaySubscription, getManageSubscriptionUrl, getTestValidityExpiry, getLatestTestValidityExpiry } from "../utils/subscriptionHelper";
import {
    getUserReadingAttempts,
    getUserWritingAttempts,
    getUserListeningAttempts,
    getUserSpeakingAttempts,
} from "../api/index";
import EngdemyLogo from "../assets/Images/EngdemyUploaded.png";
import "./Dashboard.css";

const baseURL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";



const PREDEFINED_STUDY_TASKS = [
    "Complete Reading test 1",
    "Complete Writing test 1",
    "Complete Speaking test 1",
    "Complete Listening test 1",
    "Learn 50 new vocabulary words",
    "Complete Reading test 2",
    "Complete Writing test 2",
    "Complete Speaking test 2",
    "Complete Listening test 2",
    "Learn 50 new vocabulary words",
    "Complete Reading test 3",
    "Complete Writing test 3",
    "Complete Speaking test 3",
    "Complete Listening test 3",
    "Learn 50 new vocabulary words",
    "Complete Reading test 4",
    "Complete Writing test 4",
    "Complete Speaking test 4",
    "Complete Listening test 4",
    "Learn 50 new vocabulary words",
    "Complete Reading test 5",
    "Complete Writing test 5",
    "Complete Speaking test 5",
    "Complete Listening test 5",
    "Learn 50 new vocabulary words",
    "Complete Reading test 6",
    "Complete Writing test 6",
    "Complete Speaking test 6",
    "Complete Listening test 6",
    "Learn 50 new vocabulary words",
    "Complete Reading test 7",
    "Complete Writing test 7",
    "Complete Speaking test 7",
    "Complete Listening test 7",
    "Learn 50 new vocabulary words",
    "Complete Reading test 8",
    "Complete Writing test 8",
    "Complete Speaking test 8",
    "Complete Listening test 8",
    "Learn 50 new vocabulary words",
];

function addStudyDays(date, days) {
    const copy = new Date(date);
    copy.setDate(copy.getDate() + days);
    return copy;
}

function formatStudyDate(date) {
    try {
        return date.toISOString().split("T")[0];
    } catch (e) {
        return "";
    }
}

const getStoredUser = (reduxUser) => {
    try {
        if (reduxUser?.result) return reduxUser.result;
        if (reduxUser?.email || reduxUser?._id) return reduxUser;
        const rawUser = localStorage.getItem("user_info");
        if (rawUser) {
            const localUser = JSON.parse(rawUser);
            if (localUser?.result) return localUser.result;
            if (localUser?.email || localUser?._id) return localUser;
        }
        const rawAuth = localStorage.getItem("authData");
        if (rawAuth) {
            const localAuth = JSON.parse(rawAuth);
            if (localAuth?.result) return localAuth.result;
            if (localAuth?.email || localAuth?._id) return localAuth;
        }
    } catch (e) {
        console.error("[Dashboard] Error parsing stored user:", e);
    }
    return null;
};

const Dashboard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const reduxUser = useSelector((state) => state.auth?.authData);

    const [currentUser, setCurrentUser] = useState(() => getStoredUser(reduxUser));
                                        const [playSubscription, setPlaySub] = useState(null);
    const [testFilter, setTestFilter] = useState('all');
    const [activeTab, setActiveTab] = useState('tests');

    // Detailed Performance Analytics states (CheckProgress)
    const [readingAttempts, setReadingAttempts] = useState([]);
    const [writingAttempts, setWritingAttempts] = useState([]);
    const [listeningAttempts, setListeningAttempts] = useState([]);
    const [speakingAttempts, setSpeakingAttempts] = useState([]);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsModuleFilter, setAnalyticsModuleFilter] = useState('all');

    // CELPIP Study Plan states (matching /celpipstudyplan with persistent localStorage)
    const [studyTargetDate, setStudyTargetDate] = useState(() => {
        const saved = localStorage.getItem("celpipTargetExamDate");
        if (saved) return saved;
        const d = new Date();
        d.setDate(d.getDate() + 42); // default 6 weeks ahead
        return formatStudyDate(d);
    });
    const [studyWeeksLeft, setStudyWeeksLeft] = useState(6);
    const [studyCheckedTasks, setStudyCheckedTasks] = useState({});
    const [studyCustomTasks, setStudyCustomTasks] = useState({});
    const [studyCustomTaskInput, setStudyCustomTaskInput] = useState("");
    const [studySelectedWeekForCustom, setStudySelectedWeekForCustom] = useState(1);
    const [studyHoursPerWeek, setStudyHoursPerWeek] = useState(() => {
        return parseInt(localStorage.getItem("celpipHoursPerWeek")) || 10;
    });
    const [studyTimeRemaining, setStudyTimeRemaining] = useState("");

    // Score Calculator states (mirroring /celpipscorecalculator)
    const [calcReadingScore, setCalcReadingScore] = useState('');
    const [calcListeningScore, setCalcListeningScore] = useState('');

    const selectorRef = useRef(null);
    const mockTestsRef = useRef(null);
    const location = useLocation();

    
    // Handle navigation state for scrolling and pre-selection
    useEffect(() => {
        if (location.state?.module) {
            setTimeout(() => {
                selectorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        } else if (location.state?.scrollTo === 'mockTests') {
            setTimeout(() => {
                mockTestsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [location.state]);

    useEffect(() => {
        document.title = "My Study Materials - CELPIP Portal";
        const storedUser = getStoredUser(reduxUser);
        if (storedUser) {
            setCurrentUser(storedUser);
        }
        if (!reduxUser && storedUser) {
            const raw = localStorage.getItem("user_info");
            let dataToDispatch = null;
            try {
                const parsed = raw ? JSON.parse(raw) : null;
                if (parsed?.result) dataToDispatch = parsed;
            } catch (e) {}
            if (!dataToDispatch) {
                dataToDispatch = { result: storedUser, ...storedUser };
            }
            dispatch({ type: "AUTH", data: dataToDispatch });
        }
        // Sync paidTests to localStorage when user object is loaded
        if (storedUser && Array.isArray(storedUser.paidTests)) {
            localStorage.setItem("paidTests", JSON.stringify(storedUser.paidTests));
            const isExpired = isSubscriptionExpired(storedUser);
            const hasPaid = storedUser.paidTests.length > 0;
            const hasSub = hasActiveSubscription(storedUser);
            if (isExpired || (!hasPaid && !hasSub)) {
                localStorage.removeItem("isPaidUser");
            }
        }
    }, [reduxUser, dispatch]);

    // Fetch fresh user profile on mount to ensure paidTests and subscription state are up-to-date
    useEffect(() => {
        const syncFreshUserProfile = async () => {
            const user = getStoredUser();
            if (!user?._id) return;
            try {
                const res = await fetch(`${baseURL}/users/getUserById/${user._id}`);
                if (res.ok) {
                    const freshUser = await res.json();
                    if (freshUser && freshUser._id) {
                        setCurrentUser(prev => ({ ...prev, ...freshUser }));
                        const existingInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
                        localStorage.setItem("user_info", JSON.stringify({
                            ...existingInfo,
                            result: { ...existingInfo.result, ...freshUser }
                        }));
                        if (Array.isArray(freshUser.paidTests)) {
                            localStorage.setItem("paidTests", JSON.stringify(freshUser.paidTests));
                        }
                        if (freshUser.packageName) {
                            localStorage.setItem("userPackage", freshUser.packageName);
                        } else {
                            localStorage.removeItem("userPackage");
                        }
                        const isExpired = isSubscriptionExpired(freshUser);
                        const hasPaid = Array.isArray(freshUser.paidTests) && freshUser.paidTests.length > 0;
                        const hasSub = hasActiveSubscription(freshUser);
                        if (isExpired || (!hasPaid && !hasSub)) {
                            localStorage.removeItem("isPaidUser");
                        }
                    }
                }
            } catch (err) {
                console.error("[Dashboard] Error syncing fresh user profile:", err);
            }
        };
        syncFreshUserProfile();
    }, []);

    // Check if redirected from Stripe with session_id, paid=true, or _auth_sync to auto-verify and unlock
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get("session_id");
        const paidParam = params.get("paid");
        const syncParam = params.get("_auth_sync");

        if (syncParam) {
            try {
                const decoded = decodeURIComponent(escape(atob(decodeURIComponent(syncParam))));
                const parsed = JSON.parse(decoded);
                if (parsed.user_info || parsed.result || parsed._id) {
                    const u = parsed.user_info?.result || parsed.user_info || parsed.result || parsed;
                    setCurrentUser(prev => ({ ...prev, ...u }));
                    const existingInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
                    localStorage.setItem("user_info", JSON.stringify({ ...existingInfo, result: u }));
                }
                if (parsed.paidTests) {
                    localStorage.setItem("paidTests", JSON.stringify(parsed.paidTests));
                }
                if (parsed.isPaidUser === "true" || parsed.isPaidUser === true) {
                    localStorage.setItem("isPaidUser", "true");
                }
                if (parsed.userPackage) {
                    localStorage.setItem("userPackage", parsed.userPackage);
                }
            } catch (err) {
                console.error("[Dashboard] Error decoding _auth_sync:", err);
            }
        }

        if (paidParam === "true") {
            const allTests = Array.from({ length: 50 }, (_, i) => i + 1);
            localStorage.setItem("isPaidUser", "true");
            localStorage.setItem("paidTests", JSON.stringify(allTests));
            setCurrentUser(prev => prev ? ({ ...prev, paidTests: allTests }) : prev);
        }

        if (sessionId) {
            fetch(`${baseURL}/api/stripe/session-details?session_id=${sessionId}`)
                .then(r => r.json())
                .then(data => {
                    const allTests = Array.from({ length: 50 }, (_, i) => i + 1);
                    const finalPaidTests = (data?.user?.paidTests && data.user.paidTests.length > 0)
                        ? data.user.paidTests
                        : (data?.paidTests || allTests);

                    if (data?.user) {
                        setCurrentUser({ ...data.user, paidTests: finalPaidTests });
                        const existingInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
                        localStorage.setItem("user_info", JSON.stringify({
                            ...existingInfo,
                            result: { ...data.user, paidTests: finalPaidTests }
                        }));
                    }
                    localStorage.setItem("paidTests", JSON.stringify(finalPaidTests));
                    localStorage.setItem("isPaidUser", "true");
                    if (data?.packageName) {
                        localStorage.setItem("userPackage", data.packageName);
                    }
                })
                .catch(err => console.error("[Dashboard] Failed to verify session_id:", err));
        }
    }, [location.search]);

    // Fetch Google Play subscription status on mount
    useEffect(() => {
        const fetchPlayStatus = async () => {
            if (!currentUser?._id) return;
            try {
                const res = await fetch(`${baseURL}/api/google-play/subscription-status/${currentUser._id}`);
                if (res.ok) {
                    const data = await res.json();
                    setPlaySub(data);
                    setPlaySubscription(data); // Cache in localStorage for other components
                    console.log("[Dashboard] Play subscription status:", data);
                }
            } catch (err) {
                console.error("[Dashboard] Failed to fetch Play subscription status:", err);
            }
        };
        fetchPlayStatus();
    }, [currentUser]);

    // Fetch progress data
    useEffect(() => {
        const fetchProgress = async () => {
            if (!currentUser?._id) return;
            try {
                const response = await fetch(`${baseURL}/api/progress/${currentUser._id}`);
                const data = await response.json();
                if (data.success) {
                }
            } catch (error) {
                console.error("Error fetching progress:", error);
            } finally {
            }
        };
        fetchProgress();
    }, [currentUser, navigate]);

    // Fetch detailed CheckProgress analytics for Performance Analytics tab
    useEffect(() => {
        const fetchCheckProgressData = async () => {
            if (!currentUser?._id) return;
            setAnalyticsLoading(true);
            try {
                const [readingRes, writingRes, listeningRes, speakingRes] = await Promise.allSettled([
                    getUserReadingAttempts(currentUser._id),
                    getUserWritingAttempts(currentUser._id),
                    getUserListeningAttempts(currentUser._id),
                    getUserSpeakingAttempts(currentUser._id),
                ]);

                if (readingRes.status === 'fulfilled') {
                    setReadingAttempts(readingRes.value?.attempts || []);
                }
                if (writingRes.status === 'fulfilled') {
                    setWritingAttempts(writingRes.value || []);
                }
                if (listeningRes.status === 'fulfilled') {
                    setListeningAttempts(listeningRes.value?.attempts || []);
                }
                if (speakingRes.status === 'fulfilled') {
                    const spk = speakingRes.value;
                    setSpeakingAttempts(spk?.attempts || (Array.isArray(spk) ? spk : []));
                }
            } catch (err) {
                console.error("Error fetching detailed attempts for analytics:", err);
            } finally {
                setAnalyticsLoading(false);
            }
        };

        if (activeTab === 'analytics') {
            fetchCheckProgressData();
        }
    }, [currentUser, activeTab]);

    // Study Plan: Load persistent checked & custom tasks
    useEffect(() => {
        const savedChecked = JSON.parse(localStorage.getItem("celpipCheckedTasks") || "{}");
        setStudyCheckedTasks(savedChecked);

        const savedCustom = JSON.parse(localStorage.getItem("celpipCustomTasks") || "{}");
        setStudyCustomTasks(savedCustom);
    }, []);

    // Study Plan: Save checked tasks
    useEffect(() => {
        localStorage.setItem("celpipCheckedTasks", JSON.stringify(studyCheckedTasks));
    }, [studyCheckedTasks]);

    // Study Plan: Save custom tasks
    useEffect(() => {
        localStorage.setItem("celpipCustomTasks", JSON.stringify(studyCustomTasks));
    }, [studyCustomTasks]);

    // Study Plan: Recalculate weeks left and persist target date
    useEffect(() => {
        if (!studyTargetDate) {
            setStudyWeeksLeft(0);
            return;
        }
        localStorage.setItem("celpipTargetExamDate", studyTargetDate);
        const today = new Date();
        const examDate = new Date(studyTargetDate);
        const diff = examDate - today;
        const weeks = Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24 * 7)));
        setStudyWeeksLeft(weeks);
    }, [studyTargetDate]);

    // Study Plan: Persist hours per week
    useEffect(() => {
        localStorage.setItem("celpipHoursPerWeek", studyHoursPerWeek.toString());
    }, [studyHoursPerWeek]);

    // Study Plan: Live countdown ticker
    useEffect(() => {
        if (!studyTargetDate) {
            setStudyTimeRemaining("");
            return;
        }
        const updateCountdown = () => {
            const now = new Date();
            const exam = new Date(studyTargetDate + "T23:59:59");
            const diff = exam - now;
            if (diff <= 0) {
                setStudyTimeRemaining("Exam day is here!");
                return;
            }
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((diff / (1000 * 60)) % 60);
            const seconds = Math.floor((diff / 1000) % 60);
            setStudyTimeRemaining(`${days}d ${hours}h ${minutes}m ${seconds}s`);
        };
        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [studyTargetDate]);

    // Use currentUser.paidTests directly if present; only fallback to localStorage if currentUser is not loaded yet
    const rawPaid = (currentUser && Array.isArray(currentUser.paidTests))
        ? currentUser.paidTests
        : localStorage.getItem("paidTests");
    let paidTests = [];
    if (Array.isArray(rawPaid)) {
        paidTests = rawPaid;
    } else if (typeof rawPaid === "string") {
        try {
            paidTests = JSON.parse(rawPaid);
        } catch {
            paidTests = rawPaid === "all" || rawPaid === '"all"' ? "all" : [];
        }
    }

    // Subscription Given Date & Remaining Days
    const getSubscriptionDetails = () => {
        const testValidityExpiry = getLatestTestValidityExpiry(currentUser?.testValidity);
        const rawExpiry = currentUser?.subscriptionExpiresAt || playSubscription?.expiresAt || testValidityExpiry;
        const hasSub = hasActiveSubscription(currentUser);
        const hasPaidTests = (currentUser && Array.isArray(currentUser.paidTests))
            ? currentUser.paidTests.length > 0
            : (Array.isArray(paidTests) ? paidTests.length > 0 : paidTests === "all");

        if (!rawExpiry && !hasSub && (!currentUser?.packageName && !hasPaidTests && localStorage.getItem("isPaidUser") !== "true")) {
            return {
                givenDate: "N/A",
                daysLeft: 0,
                daysLeftText: "Free Test",
                expiryText: "Free Test",
                isExpired: false
            };
        }

        const rawGiven = currentUser?.subscriptionStartedAt || currentUser?.createdAt || playSubscription?.startedAt;
        let formattedGiven = "N/A";
        try {
            if (rawGiven) {
                const d = new Date(rawGiven);
                if (!isNaN(d.getTime())) {
                    formattedGiven = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                }
            }
        } catch (e) {
            formattedGiven = "N/A";
        }

        let daysLeft = 0;
        let formattedExpiry = "N/A";
        let isExpired = false;
        try {
            let exp = rawExpiry ? new Date(rawExpiry) : null;
            if ((!exp || isNaN(exp.getTime())) && (currentUser?.packageName || localStorage.getItem("isPaidUser") === "true")) {
                const start = new Date(rawGiven || Date.now());
                exp = new Date(start.getTime() + 15 * 24 * 60 * 60 * 1000);
            }
            if (exp && !isNaN(exp.getTime())) {
                const now = new Date();
                const diff = exp.getTime() - now.getTime();
                daysLeft = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
                formattedExpiry = exp.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                isExpired = diff <= 0;
            }
        } catch (e) {
            daysLeft = 0;
            isExpired = true;
        }

        if (!isExpired && isSubscriptionExpired(currentUser)) {
            isExpired = true;
            daysLeft = 0;
        }

        return {
            givenDate: formattedGiven,
            daysLeft: daysLeft,
            daysLeftText: daysLeft > 0 ? `${daysLeft} Days Left` : "Expired",
            expiryText: formattedExpiry,
            isExpired: isExpired || (rawExpiry && new Date(rawExpiry) <= new Date())
        };
    };

    const subDetails = getSubscriptionDetails();
    const isUserSubscribed = !subDetails.isExpired && (hasActiveSubscription(currentUser) || subDetails.daysLeft > 0);
    const hasUserEverPurchased = Boolean(
        currentUser?.subscriptionExpiresAt ||
        currentUser?.subscriptionStartedAt ||
        currentUser?.packageName ||
        (currentUser?.paidTests && currentUser.paidTests.length > 0) ||
        (currentUser?.testValidity && Object.keys(currentUser.testValidity).length > 0) ||
        playSubscription?.expiresAt ||
        playSubscription?.productId ||
        localStorage.getItem("isPaidUser") === "true"
    );

    const isTestActive = (testNum) => {
        // If access/subscription or validity is expired, paid tests (3-50) CANNOT be started (no start test should be there)
        if (subDetails.isExpired || isSubscriptionExpired(currentUser)) return false;

        // Check specific test expiry from testValidity
        if (currentUser?.testValidity) {
            const exp = getTestValidityExpiry(currentUser.testValidity, testNum);
            if (exp && exp <= new Date()) {
                return false;
            }
        }

        // Tests 1-2 are free tests (available if validity is not expired)
        if (testNum <= 2) return true;

        // Active verified subscription unlocks all tests
        if (isUserSubscribed) return true;

        // If user has zero paid tests, paid tests (3-50) CANNOT be started - no start test should be there
        if (!paidTests || (Array.isArray(paidTests) && paidTests.length === 0)) {
            return false;
        }

        if (paidTests === "all") return true;
        if (Array.isArray(paidTests) && (paidTests.includes(testNum) || paidTests.includes(Number(testNum)))) return true;
        return isTestUnlocked(testNum, currentUser);
    };

    // Centralized lock logic — checks subscription validity + Stripe paidTests + Google Play
    const mockTests = Array.from({ length: 50 }, (_, i) => ({
        id: i + 1,
        isLocked: !isTestActive(i + 1),
    }));

    const handleTestStart = (testId) => {
        if (isTestActive(testId)) {
            navigate(`/fulltest/${testId}`);
        } else {
            alert(subDetails.isExpired
                ? "Your subscription has expired. Please renew your plan to start this test."
                : "This test is locked. Please upgrade your account to access it.");
            navigate("/pricing?category=tests");
        }
    };

    
    const handleBuyNow = () => navigate("/pricing?category=tests");

    
    
    
    
    
    
    
    const filteredMockTests = mockTests.filter((test) => {
        if (testFilter === 'unlocked') return !test.isLocked;
        if (testFilter === 'locked') return test.isLocked;
        return true;
    });

    const unlockedCount = mockTests.filter(t => !t.isLocked).length;
    const lockedCount = mockTests.filter(t => t.isLocked).length;

    // CheckProgress Data Processors
    const preprocessWritingAttempts = (attempts) => {
        const grouped = {};
        (attempts || []).forEach((att) => {
            const testNum = att.testNumber;
            if (!grouped[testNum]) grouped[testNum] = {};
            if (att.taskNumber == 1) grouped[testNum].task1 = att;
            if (att.taskNumber == 2) grouped[testNum].task2 = att;
        });
        return grouped;
    };

    const preprocessSpeakingAttempts = (attempts) => {
        const grouped = {};
        (attempts || []).forEach(att => {
            const question = att.questionId;
            if (!question || typeof question !== "object") return;
            const testNum = question.testNumber;
            const taskNum = question.taskNumber;
            if (!testNum || !taskNum) return;
            if (!grouped[testNum]) grouped[testNum] = {};
            grouped[testNum][`task${taskNum}`] = att;
        });
        return grouped;
    };

    const renderReadingAnalytics = () => {
        const items = [];
        for (let i = 1; i <= 50; i++) {
            const testAttempts = (readingAttempts || []).filter(a => a.testNumber === i);
            const hasAttempt = testAttempts.length > 0;
            const totalScore = testAttempts.reduce((sum, a) => sum + (a.attemptScore || 0), 0);
            const latestAttempt = hasAttempt
                ? [...testAttempts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
                : null;

            items.push(
                <div key={i} className="paragon-attempt-item">
                    <div className="paragon-attempt-header">
                        <span style={{ color: "#059669" }}>Test {i}</span>
                        {hasAttempt && (
                            <span className="paragon-days-left-pill" style={{ background: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0' }}>
                                CLB {latestAttempt.clbScore ?? "N/A"}
                            </span>
                        )}
                    </div>
                    {hasAttempt ? (
                        <>
                            <div className="paragon-attempt-meta">
                                <div>📅 {new Date(latestAttempt.createdAt).toLocaleDateString()}</div>
                                <div>🏆 Total Score: <strong>{totalScore}</strong></div>
                            </div>
                            <button
                                className="paragon-view-results-btn"
                                onClick={() => navigate(`/reading/${i}`, {
                                    state: {
                                        showResults: true,
                                        savedAttempts: testAttempts,
                                    }
                                })}
                            >
                                📄 View Results
                            </button>
                        </>
                    ) : (
                        <div className="paragon-not-attempted">
                            <span>Not attempted yet</span>
                            <span
                                className="paragon-start-drill-link"
                                onClick={() => {
                                    if (isTestActive(i)) {
                                        navigate(`/reading/${i}`);
                                    } else {
                                        handleBuyNow();
                                    }
                                }}
                            >
                                {isTestActive(i) ? "Start Test" : "Locked"}
                            </span>
                        </div>
                    )}
                </div>
            );
        }
        return items;
    };

    const renderListeningAnalytics = () => {
        const items = [];
        for (let i = 1; i <= 50; i++) {
            const testAttempts = (listeningAttempts || []).filter(a => a.testNumber === i);
            const hasAttempt = testAttempts.length > 0;
            const latestAttempt = hasAttempt
                ? [...testAttempts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
                : null;

            items.push(
                <div key={i} className="paragon-attempt-item">
                    <div className="paragon-attempt-header">
                        <span style={{ color: "#0284c7" }}>Test {i}</span>
                        {hasAttempt && (
                            <span className="paragon-days-left-pill" style={{ background: '#f0f9ff', color: '#0369a1', borderColor: '#bae6fd' }}>
                                CLB {latestAttempt.clbScore ?? "N/A"}
                            </span>
                        )}
                    </div>
                    {hasAttempt ? (
                        <>
                            <div className="paragon-attempt-meta">
                                <div>📅 {new Date(latestAttempt.createdAt).toLocaleDateString()}</div>
                                <div>🏆 Score: <strong>{latestAttempt.attemptScore ?? "N/A"}</strong></div>
                            </div>
                            <button
                                className="paragon-view-results-btn"
                                onClick={() => navigate(`/listening/${i}`, {
                                    state: {
                                        showResults: true,
                                        savedAttempt: latestAttempt,
                                    }
                                })}
                            >
                                📄 View Results
                            </button>
                        </>
                    ) : (
                        <div className="paragon-not-attempted">
                            <span>Not attempted yet</span>
                            <span
                                className="paragon-start-drill-link"
                                onClick={() => {
                                    if (isTestActive(i)) {
                                        navigate(`/listening/${i}`);
                                    } else {
                                        handleBuyNow();
                                    }
                                }}
                            >
                                {isTestActive(i) ? "Start Test" : "Locked"}
                            </span>
                        </div>
                    )}
                </div>
            );
        }
        return items;
    };

    const renderWritingAnalytics = () => {
        const groupedAttempts = preprocessWritingAttempts(writingAttempts);
        const items = [];
        for (let i = 1; i <= 50; i++) {
            const test = groupedAttempts[i] || {};
            const task1Score = test.task1?.scores?.overallScore ?? null;
            const task2Score = test.task2?.scores?.overallScore ?? null;
            const hasAttempt = task1Score != null || task2Score != null;
            const overallScore = task1Score != null && task2Score != null
                ? Math.round((task1Score + task2Score) / 2)
                : null;
            const lastDate = test.task2?.createdAt ?? test.task1?.createdAt;

            items.push(
                <div key={i} className="paragon-attempt-item">
                    <div className="paragon-attempt-header">
                        <span style={{ color: "#d97706" }}>Test {i}</span>
                        {overallScore != null && (
                            <span className="paragon-days-left-pill" style={{ background: '#fffbeb', color: '#b45309', borderColor: '#fde68a' }}>
                                Score: {overallScore}/12
                            </span>
                        )}
                    </div>
                    {hasAttempt ? (
                        <>
                            <div className="paragon-attempt-meta">
                                <div>📝 Task 1: {task1Score != null ? Math.round(task1Score) : "Pending"} | Task 2: {task2Score != null ? Math.round(task2Score) : "Pending"}</div>
                                {lastDate && <div>📅 {new Date(lastDate).toLocaleDateString()}</div>}
                            </div>
                            <button
                                className="paragon-view-results-btn"
                                onClick={() => navigate(`/writing/${i}`, {
                                    state: {
                                        showResults: true,
                                        savedAttempts: test
                                    }
                                })}
                            >
                                📄 View Results
                            </button>
                        </>
                    ) : (
                        <div className="paragon-not-attempted">
                            <span>Not attempted yet</span>
                            <span
                                className="paragon-start-drill-link"
                                onClick={() => {
                                    if (isTestActive(i)) {
                                        navigate(`/writing/${i}`);
                                    } else {
                                        handleBuyNow();
                                    }
                                }}
                            >
                                {isTestActive(i) ? "Start Test" : "Locked"}
                            </span>
                        </div>
                    )}
                </div>
            );
        }
        return items;
    };

    const renderSpeakingAnalytics = () => {
        const groupedAttempts = preprocessSpeakingAttempts(speakingAttempts);
        const items = [];
        for (let i = 1; i <= 50; i++) {
            const test = groupedAttempts[i] || {};
            const validScores = Object.values(test)
                .map(a => a?.scores?.overallScore)
                .filter(s => s != null);
            const hasAttempt = validScores.length > 0;
            const overallScore = validScores.length
                ? (validScores.reduce((a, b) => a + b, 0) / validScores.length).toFixed(1)
                : null;
            const lastAttemptDate = Object.values(test)
                .reduce((latest, att) => {
                    if (!att?.attemptDate) return latest;
                    return !latest || new Date(att.attemptDate) > new Date(latest)
                        ? att.attemptDate
                        : latest;
                }, null);

            items.push(
                <div key={i} className="paragon-attempt-item">
                    <div className="paragon-attempt-header">
                        <span style={{ color: "#800000" }}>Test {i}</span>
                        {overallScore != null && (
                            <span className="paragon-days-left-pill" style={{ background: '#fde8ec', color: '#800000', borderColor: 'rgba(128, 0, 0, 0.3)' }}>
                                Avg: {overallScore}/12
                            </span>
                        )}
                    </div>
                    {hasAttempt ? (
                        <>
                            <div className="paragon-attempt-meta">
                                <div>🗣️ Tasks Evaluated: {validScores.length}/8</div>
                                {lastAttemptDate && <div>📅 {new Date(lastAttemptDate).toLocaleDateString()}</div>}
                            </div>
                            <button
                                className="paragon-view-results-btn"
                                onClick={() => navigate(`/speaking/${i}`, {
                                    state: {
                                        showResults: true,
                                        savedAttempts: Object.values(test)
                                    }
                                })}
                            >
                                📄 View Results
                            </button>
                        </>
                    ) : (
                        <div className="paragon-not-attempted">
                            <span>Not attempted yet</span>
                            <span
                                className="paragon-start-drill-link"
                                onClick={() => {
                                    if (isTestActive(i)) {
                                        navigate(`/speaking/${i}`);
                                    } else {
                                        handleBuyNow();
                                    }
                                }}
                            >
                                {isTestActive(i) ? "Start Test" : "Locked"}
                            </span>
                        </div>
                    )}
                </div>
            );
        }
        return items;
    };

    // Study Plan Helpers & Calculations
    const distributeStudyTasksByWeek = () => {
        if (studyWeeksLeft === 0) return [];
        let plan = Array.from({ length: studyWeeksLeft }, () => []);
        const today = new Date();

        PREDEFINED_STUDY_TASKS.forEach((task, i) => {
            const weekIndex = Math.min(studyWeeksLeft - 1, Math.floor((i / PREDEFINED_STUDY_TASKS.length) * studyWeeksLeft));
            const deadlineDate = addStudyDays(today, (weekIndex + 1) * 7);
            plan[weekIndex].push({
                id: `predef-${i + 1}`,
                text: task,
                deadline: formatStudyDate(deadlineDate),
            });
        });

        Object.entries(studyCustomTasks).forEach(([weekStr, tasks]) => {
            const wNum = parseInt(weekStr.replace("week", ""));
            if (plan[wNum - 1] && Array.isArray(tasks)) {
                tasks.forEach((t) => plan[wNum - 1].push(t));
            }
        });

        return plan.map((tasks, idx) => ({
            weekNumber: idx + 1,
            tasks,
        }));
    };

    const toggleStudyTask = (taskId) => {
        setStudyCheckedTasks((prev) => ({
            ...prev,
            [taskId]: !prev[taskId],
        }));
    };

    const addStudyCustomTask = () => {
        if (!studyCustomTaskInput.trim()) return;
        const key = `week${studySelectedWeekForCustom}`;
        const newTask = {
            id: `custom-${Date.now()}`,
            text: studyCustomTaskInput.trim(),
            deadline: formatStudyDate(addStudyDays(new Date(), studySelectedWeekForCustom * 7))
        };
        setStudyCustomTasks((prev) => ({
            ...prev,
            [key]: prev[key] ? [...prev[key], newTask] : [newTask],
        }));
        setStudyCustomTaskInput("");
    };

    const parseTestLinkFromTask = (text) => {
        const readingMatch = text.match(/reading test (\d+)/i);
        if (readingMatch) return { label: `Start Reading ${readingMatch[1]}`, path: `/reading/${readingMatch[1]}` };
        const listeningMatch = text.match(/listening test (\d+)/i);
        if (listeningMatch) return { label: `Start Listening ${listeningMatch[1]}`, path: `/listening/${listeningMatch[1]}` };
        const writingMatch = text.match(/writing test (\d+)/i);
        if (writingMatch) return { label: `Start Writing ${writingMatch[1]}`, path: `/writing/${writingMatch[1]}` };
        const speakingMatch = text.match(/speaking test (\d+)/i);
        if (speakingMatch) return { label: `Start Speaking ${speakingMatch[1]}`, path: `/speaking/${speakingMatch[1]}` };
        return null;
    };

    const weeklyStudyPlan = distributeStudyTasksByWeek();
    const totalStudyTasksCount = weeklyStudyPlan.reduce((sum, w) => sum + w.tasks.length, 0);
    const completedStudyTasksCount = Object.values(studyCheckedTasks).filter(Boolean).length;
    const studyProgressPercent = totalStudyTasksCount
        ? Math.round((completedStudyTasksCount / totalStudyTasksCount) * 100)
        : 0;

    // Score Calculator logic (Reading & Listening to CELPIP Level)
    const getReadingLevel = (score) => {
        if (score === '' || score === null || score === undefined) return "—";
        const s = Number(score);
        if (isNaN(s)) return "—";
        if (s >= 33 && s <= 38) return "10–12";
        if (s >= 31 && s <= 32) return "9";
        if (s >= 28 && s <= 30) return "8";
        if (s >= 24 && s <= 27) return "7";
        if (s >= 19 && s <= 23) return "6";
        if (s >= 15 && s <= 18) return "5";
        if (s >= 10 && s <= 14) return "4";
        if (s >= 8 && s <= 9) return "3";
        if (s >= 0) return "M";
        return "—";
    };

    const getListeningLevel = (score) => {
        if (score === '' || score === null || score === undefined) return "—";
        const s = Number(score);
        if (isNaN(s)) return "—";
        if (s >= 35 && s <= 38) return "10–12";
        if (s >= 33 && s <= 34) return "9";
        if (s >= 30 && s <= 32) return "8";
        if (s >= 27 && s <= 29) return "7";
        if (s >= 22 && s <= 26) return "6";
        if (s >= 17 && s <= 21) return "5";
        if (s >= 11 && s <= 16) return "4";
        if (s >= 7 && s <= 10) return "3";
        if (s >= 0) return "M";
        return "—";
    };

    return (
        <div className="paragon-portal-wrapper">

            {/* ── TOP OFFICIAL HEADER BAR WITH ENGDEMY LOGO ── */}
            <div className="paragon-header">
                <div className="paragon-logo-wrap" onClick={() => setActiveTab('tests')}>
                    <img src={EngdemyLogo} alt="Engdemy" className="paragon-engdemy-logo" />
                </div>

                <div className="paragon-user-nav">
                    <div className="paragon-cart-icon" title="Shopping Cart / Pricing" onClick={() => navigate("/pricing")}>
                        <FaShoppingCart />
                    </div>
                    <div 
                        className="paragon-user-badge"
                        onClick={() => navigate(currentUser ? "/profile" : "/login")}
                        style={{ cursor: "pointer" }}
                        title={currentUser?.email ? `Logged in as ${currentUser.email}` : "Click to log in"}
                    >
                        <FaUserCircle className="paragon-user-avatar" />
                        <span className="paragon-user-email">
                            {currentUser?.email || currentUser?.name || (currentUser ? "My Account" : "Sign In")}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── SECONDARY NAVIGATION TABS (Test 1-50, Study Plan, Score Calculator, Performance Analytics) ── */}
            <div className="paragon-tabs-bar">
                <button
                    className={`paragon-tab-btn ${activeTab === 'tests' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tests')}
                >
                    Test 1–50
                </button>
                <button
                    className={`paragon-tab-btn ${activeTab === 'studyplan' ? 'active' : ''}`}
                    onClick={() => setActiveTab('studyplan')}
                >
                    Study Plan
                </button>
                <button
                    className={`paragon-tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
                    onClick={() => setActiveTab('calculator')}
                >
                    Score Calculator
                </button>
                <button
                    className={`paragon-tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
                    onClick={() => setActiveTab('analytics')}
                >
                    Performance Analytics
                </button>
            </div>

            {/* ── MAIN CONTENT CONTAINER ── */}
            <div className="paragon-content-container">

                {/* TAB 1: PRACTICE TESTS TABLE */}
                {activeTab === 'tests' && (
                    <div>
                        {/* Section Subheader with Filter Buttons */}
                        <div className="paragon-section-header">
                            <div>
                                <h2 className="paragon-section-title">My Study Materials</h2>
                            </div>
                            <div className="paragon-filter-group">
                                <button
                                    className={`paragon-filter-btn ${testFilter === 'all' ? 'active' : ''}`}
                                    onClick={() => setTestFilter('all')}
                                >
                                    All (50)
                                </button>
                                <button
                                    className={`paragon-filter-btn ${testFilter === 'unlocked' ? 'active' : ''}`}
                                    onClick={() => setTestFilter('unlocked')}
                                >
                                    Active / Free ({unlockedCount})
                                </button>
                                <button
                                    className={`paragon-filter-btn ${testFilter === 'locked' ? 'active' : ''}`}
                                    onClick={() => setTestFilter('locked')}
                                >
                                    {hasUserEverPurchased ? 'Expired' : 'Locked'} ({lockedCount})
                                </button>
                            </div>
                        </div>

                        {/* Subscription Summary Banner */}
                        {isUserSubscribed ? (
                            <div className="paragon-sub-summary-banner">
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-sub-summary-label">Subscription Given:</span>
                                    <strong className="paragon-sub-summary-value">{subDetails.givenDate}</strong>
                                </div>
                                <div className="paragon-sub-summary-divider">|</div>
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-sub-summary-label">Valid Until:</span>
                                    <strong className="paragon-sub-summary-value">{subDetails.expiryText}</strong>
                                </div>
                                <div className="paragon-sub-summary-divider">|</div>
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-days-left-pill prominent">{subDetails.daysLeftText}</span>
                                </div>
                            </div>
                        ) : hasUserEverPurchased ? (
                            <div className="paragon-sub-summary-banner">
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-sub-summary-label">Status:</span>
                                    <strong className="paragon-sub-summary-value" style={{ color: '#800000' }}>Subscription Expired</strong>
                                </div>
                                <div className="paragon-sub-summary-divider">|</div>
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-sub-summary-label">Expired On:</span>
                                    <strong className="paragon-sub-summary-value">{subDetails.expiryText}</strong>
                                </div>
                                <div className="paragon-sub-summary-divider">|</div>
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-days-left-pill prominent">Expired</span>
                                </div>
                            </div>
                        ) : (
                            <div className="paragon-sub-summary-banner">
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-sub-summary-label">Access Level:</span>
                                    <strong className="paragon-sub-summary-value">Free Test (1 &amp; 2)</strong>
                                </div>
                                <div className="paragon-sub-summary-divider">|</div>
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-sub-summary-label">Validity:</span>
                                    <strong className="paragon-sub-summary-value">Free Test</strong>
                                </div>
                                <div className="paragon-sub-summary-divider">|</div>
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-days-left-pill prominent">Free Test</span>
                                </div>
                            </div>
                        )}

                        {/* Google Play Subscription Badge if active */}
                        {playSubscription?.isActive && (
                            <div style={{
                                display: "flex", alignItems: "center", gap: 10,
                                background: "#ecfdf5", border: "1px solid #a7f3d0",
                                color: "#065f46", padding: "10px 16px", borderRadius: 4,
                                marginBottom: 16, fontSize: 13, fontWeight: 600,
                            }}>
                                <span>✅ Active Verified Subscription</span>
                                {playSubscription.expiresAt && (
                                    <span style={{ fontSize: 12, color: "#047857" }}>
                                        · Renews {new Date(playSubscription.expiresAt).toLocaleDateString()}
                                    </span>
                                )}
                                <a
                                    href={getManageSubscriptionUrl()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: "#059669", textDecoration: "underline",
                                        fontSize: 12, marginLeft: "auto", fontWeight: 700,
                                    }}
                                >
                                    Manage Subscription
                                </a>
                            </div>
                        )}

                        {/* Practice Tests Table */}
                        <table className="paragon-tests-table">
                            <tbody>
                                {filteredMockTests.map((test) => {
                                    const isFreeOrActive = !test.isLocked;
                                    return (
                                        <tr key={test.id} className="paragon-test-row">
                                            {/* Status / Expiry Column: Given Date & Days Left */}
                                            <td className="paragon-cell-status">
                                                {test.id <= 2 && !isUserSubscribed ? (
                                                    <span className="paragon-free-status-tag">Free Test</span>
                                                ) : isFreeOrActive ? (
                                                    <div className="paragon-status-card">
                                                        <div className="paragon-given-text">
                                                            <span className="paragon-status-lbl">Given:</span> {subDetails.givenDate}
                                                        </div>
                                                        <div className="paragon-days-left-pill">
                                                            {subDetails.daysLeftText}
                                                        </div>
                                                        <div className="paragon-tz-text">
                                                            Expires: {subDetails.expiryText}
                                                        </div>
                                                    </div>
                                                ) : subDetails.isExpired ? (
                                                    <div className="paragon-status-card">
                                                        <div className="paragon-given-text">
                                                            <span className="paragon-status-lbl">Given:</span> {subDetails.givenDate}
                                                        </div>
                                                        <div className="paragon-days-left-pill prominent" style={{ background: '#fde8ec', color: '#800000', borderColor: 'rgba(128, 0, 0, 0.3)' }}>
                                                            Expired
                                                        </div>
                                                        <div className="paragon-tz-text">
                                                            Expires: {subDetails.expiryText}
                                                        </div>
                                                    </div>
                                                ) : hasUserEverPurchased ? (
                                                    <span className="paragon-expired">Expired</span>
                                                ) : (
                                                    <span className="paragon-locked-tag">Locked</span>
                                                )}
                                            </td>

                                            {/* Product Title & Subtitle Column */}
                                            <td className="paragon-cell-info">
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                        <span
                                                            className="paragon-product-link"
                                                            onClick={() => {
                                                                 if (isFreeOrActive) {
                                                                    handleTestStart(test.id);
                                                                } else {
                                                                    handleBuyNow();
                                                                }
                                                            }}
                                                        >
                                                            {test.id === 1
                                                                ? "Free CELPIP-General Practice Test 1"
                                                                : test.id === 2
                                                                ? "Free CELPIP-General Practice Test 2"
                                                                : `CELPIP-General Practice Test ${test.id}`}
                                                        </span>
                                                        {test.id <= 2 && (
                                                            <span className="paragon-free-badge">
                                                                Free Test
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="paragon-product-subtext">
                                                        Electronic Product (Online)
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Action Column */}
                                            <td className="paragon-cell-actions">
                                                {isFreeOrActive ? (
                                                    <div className="paragon-actions-group">
                                                        <button
                                                            className="paragon-start-btn"
                                                            onClick={() => handleTestStart(test.id)}
                                                            title={`Start Test ${test.id} Full Test`}
                                                        >
                                                            <FaPlay style={{ fontSize: 10 }} />
                                                            <span>Start Test</span>
                                                        </button>
                                                        <button
                                                            className="paragon-mod-btn"
                                                            onClick={() => navigate(`/listening/${test.id}`)}
                                                            title="Practice Listening"
                                                        >
                                                            L
                                                        </button>
                                                        <button
                                                            className="paragon-mod-btn"
                                                            onClick={() => navigate(`/reading/${test.id}`)}
                                                            title="Practice Reading"
                                                        >
                                                            R
                                                        </button>
                                                        <button
                                                            className="paragon-mod-btn"
                                                            onClick={() => navigate(`/writing/${test.id}`)}
                                                            title="Practice Writing"
                                                        >
                                                            W
                                                        </button>
                                                        <button
                                                            className="paragon-mod-btn"
                                                            onClick={() => navigate(`/speaking/${test.id}`)}
                                                            title="Practice Speaking"
                                                        >
                                                            S
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        className="paragon-buy-btn"
                                                        onClick={handleBuyNow}
                                                    >
                                                        Buy Now
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* TAB 2: CELPIP STUDY PLANNER */}
                {activeTab === 'studyplan' && (
                    <div className="paragon-studyplan-section">
                        {/* Section Header */}
                        <div className="paragon-section-header">
                            <div>
                                <h2 className="paragon-section-title">CELPIP Study Planner</h2>
                                <p style={{ fontSize: '15px', color: '#6b7280', margin: '6px 0 0' }}>
                                    Personalized preparation roadmap, milestone tracking, and weekly goal management.
                                </p>
                            </div>
                            {studyTimeRemaining && (
                                <div className="paragon-study-countdown-badge">
                                    <FaClock style={{ color: '#800000' }} />
                                    <span>Time to Exam: <strong>{studyTimeRemaining}</strong></span>
                                </div>
                            )}
                        </div>

                        {/* Configuration Bar: Target Date, Hours/Week, and Progress Overview */}
                        <div className="paragon-studyplan-controls-card">
                            <div className="paragon-studyplan-input-group">
                                <label className="paragon-studyplan-label">
                                    <FaCalendarAlt style={{ marginRight: 6, color: '#800000' }} />
                                    Target Exam Date:
                                </label>
                                <input
                                    type="date"
                                    value={studyTargetDate}
                                    onChange={(e) => setStudyTargetDate(e.target.value)}
                                    className="paragon-studyplan-date-input"
                                    min={new Date().toISOString().split("T")[0]}
                                />
                            </div>

                            <div className="paragon-studyplan-input-group">
                                <label className="paragon-studyplan-label">
                                    <FaClock style={{ marginRight: 6, color: '#800000' }} />
                                    Hours Available / Week:
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    max={80}
                                    value={studyHoursPerWeek}
                                    onChange={(e) => setStudyHoursPerWeek(parseInt(e.target.value) || 1)}
                                    className="paragon-studyplan-num-input"
                                />
                            </div>

                            <div className="paragon-studyplan-stat-box">
                                <div className="paragon-studyplan-stat-label">Timeline</div>
                                <div className="paragon-studyplan-stat-value">{studyWeeksLeft} Weeks</div>
                            </div>

                            <div className="paragon-studyplan-stat-box">
                                <div className="paragon-studyplan-stat-label">Completion</div>
                                <div className="paragon-studyplan-stat-value">{completedStudyTasksCount}/{totalStudyTasksCount} ({studyProgressPercent}%)</div>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="paragon-studyplan-progress-wrap">
                            <div className="paragon-studyplan-progress-fill" style={{ width: `${studyProgressPercent}%` }} />
                        </div>

                        {/* Custom Task Creator Bar */}
                        {studyWeeksLeft > 0 && (
                            <div className="paragon-studyplan-custom-creator">
                                <span className="paragon-studyplan-creator-label">
                                    <FaPlus style={{ marginRight: 6, color: '#800000' }} />
                                    Add Task:
                                </span>
                                <select
                                    value={studySelectedWeekForCustom}
                                    onChange={(e) => setStudySelectedWeekForCustom(parseInt(e.target.value))}
                                    className="paragon-studyplan-select"
                                >
                                    {Array.from({ length: studyWeeksLeft }, (_, i) => i + 1).map((w) => (
                                        <option key={`week-opt-${w}`} value={w}>
                                            Week {w}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    placeholder="e.g. Practice Reading Test 1 and review vocabulary"
                                    value={studyCustomTaskInput}
                                    onChange={(e) => setStudyCustomTaskInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') addStudyCustomTask(); }}
                                    className="paragon-studyplan-task-input"
                                />
                                <button onClick={addStudyCustomTask} className="paragon-studyplan-add-btn">
                                    Add Task
                                </button>
                            </div>
                        )}

                        {/* Weekly Plan Cards Grid */}
                        <div className="paragon-studyplan-weeks-grid">
                            {weeklyStudyPlan.map(({ weekNumber, tasks }) => {
                                const weekCompleted = tasks.filter(t => studyCheckedTasks[t.id]).length;
                                return (
                                    <div key={`study-week-${weekNumber}`} className="paragon-study-week-card">
                                        <div className="paragon-study-week-header">
                                            <div className="paragon-study-week-title">
                                                <span>Week {weekNumber}</span>
                                            </div>
                                            <span className="paragon-study-week-badge">
                                                {weekCompleted} / {tasks.length} Done
                                            </span>
                                        </div>

                                        <div className="paragon-study-task-list">
                                            {tasks.map(({ id, text, deadline }) => {
                                                const isChecked = !!studyCheckedTasks[id];
                                                const testLink = parseTestLinkFromTask(text);
                                                return (
                                                    <div key={id} className={`paragon-study-task-item ${isChecked ? 'completed' : ''}`}>
                                                        <label className="paragon-study-task-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={() => toggleStudyTask(id)}
                                                                className="paragon-study-task-checkbox"
                                                            />
                                                            <span className="paragon-study-task-text">{text}</span>
                                                        </label>

                                                        <div className="paragon-study-task-actions">
                                                            {deadline && (
                                                                <span className="paragon-study-task-due">
                                                                    Due: {deadline}
                                                                </span>
                                                            )}
                                                            {testLink && !isChecked && (
                                                                <button
                                                                    className="paragon-study-launch-btn"
                                                                    onClick={() => navigate(testLink.path)}
                                                                    title={`Navigate to test`}
                                                                >
                                                                    <FaPlay style={{ fontSize: 9 }} />
                                                                    <span>{testLink.label}</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* TAB 3: SCORE CALCULATOR */}
                {activeTab === 'calculator' && (
                    <div className="paragon-calculator-section">
                        {/* Section Header */}
                        <div className="paragon-section-header">
                            <div>
                                <h2 className="paragon-section-title">CELPIP Reading &amp; Listening Score Calculator</h2>
                                <p style={{ fontSize: '15px', color: '#6b7280', margin: '6px 0 0' }}>
                                    Convert raw practice test scores (out of 38) into official Canadian Language Benchmark (CLB) levels.
                                </p>
                            </div>
                            <div className="paragon-calculator-badge">
                                Official CLB Diagnostic Tool
                            </div>
                        </div>

                        {/* 2-Column Calculator Cards Grid */}
                        <div className="paragon-calc-grid">
                            {/* Reading Calculator Card */}
                            <div className="paragon-calc-card">
                                <div className="paragon-calc-card-header">
                                    <div className="paragon-calc-module-icon reading">📖</div>
                                    <div>
                                        <h3 className="paragon-calc-card-title">Reading Score Calculator</h3>
                                        <div className="paragon-calc-card-sub">4 Parts · 38 Questions total</div>
                                    </div>
                                </div>

                                <div className="paragon-calc-input-row">
                                    <label className="paragon-calc-label">Enter Correct Answers (0–38):</label>
                                    <div className="paragon-calc-input-wrapper">
                                        <input
                                            type="number"
                                            min="0"
                                            max="38"
                                            value={calcReadingScore}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === '' || (Number(val) >= 0 && Number(val) <= 38)) {
                                                    setCalcReadingScore(val);
                                                }
                                            }}
                                            placeholder="e.g. 31"
                                            className="paragon-calc-number-input"
                                        />
                                        <span className="paragon-calc-total-tag">/ 38</span>
                                    </div>
                                </div>

                                {/* Score Slider */}
                                <input
                                    type="range"
                                    min="0"
                                    max="38"
                                    value={calcReadingScore === '' ? 0 : Number(calcReadingScore)}
                                    onChange={(e) => setCalcReadingScore(e.target.value)}
                                    className="paragon-calc-range-slider"
                                />

                                {/* Output CLB Box */}
                                <div className="paragon-calc-result-box reading">
                                    <div className="paragon-calc-result-label">Calculated CELPIP / CLB Level</div>
                                    <div className="paragon-calc-result-value">
                                        {calcReadingScore === '' ? '—' : `CLB ${getReadingLevel(calcReadingScore)}`}
                                    </div>
                                    <div className="paragon-calc-result-note">
                                        {calcReadingScore !== '' && Number(calcReadingScore) >= 31 && (
                                            <span style={{ color: '#047857' }}>🌟 Excellent! Meets CLB 9+ requirement for maximum Express Entry CRS points.</span>
                                        )}
                                        {calcReadingScore !== '' && Number(calcReadingScore) >= 24 && Number(calcReadingScore) < 31 && (
                                            <span style={{ color: '#b45309' }}>👍 Good standing! Meets CLB 7–8 standard PR requirements.</span>
                                        )}
                                        {calcReadingScore !== '' && Number(calcReadingScore) < 24 && (
                                            <span style={{ color: '#800000' }}>⚡ Needs practice. Recommend completing more practice tests.</span>
                                        )}
                                        {calcReadingScore === '' && (
                                            <span>Enter your score above to calculate level.</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Listening Calculator Card */}
                            <div className="paragon-calc-card">
                                <div className="paragon-calc-card-header">
                                    <div className="paragon-calc-module-icon listening">🎧</div>
                                    <div>
                                        <h3 className="paragon-calc-card-title">Listening Score Calculator</h3>
                                        <div className="paragon-calc-card-sub">6 Parts · 38 Questions total</div>
                                    </div>
                                </div>

                                <div className="paragon-calc-input-row">
                                    <label className="paragon-calc-label">Enter Correct Answers (0–38):</label>
                                    <div className="paragon-calc-input-wrapper">
                                        <input
                                            type="number"
                                            min="0"
                                            max="38"
                                            value={calcListeningScore}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === '' || (Number(val) >= 0 && Number(val) <= 38)) {
                                                    setCalcListeningScore(val);
                                                }
                                            }}
                                            placeholder="e.g. 33"
                                            className="paragon-calc-number-input"
                                        />
                                        <span className="paragon-calc-total-tag">/ 38</span>
                                    </div>
                                </div>

                                {/* Score Slider */}
                                <input
                                    type="range"
                                    min="0"
                                    max="38"
                                    value={calcListeningScore === '' ? 0 : Number(calcListeningScore)}
                                    onChange={(e) => setCalcListeningScore(e.target.value)}
                                    className="paragon-calc-range-slider"
                                />

                                {/* Output CLB Box */}
                                <div className="paragon-calc-result-box listening">
                                    <div className="paragon-calc-result-label">Calculated CELPIP / CLB Level</div>
                                    <div className="paragon-calc-result-value">
                                        {calcListeningScore === '' ? '—' : `CLB ${getListeningLevel(calcListeningScore)}`}
                                    </div>
                                    <div className="paragon-calc-result-note">
                                        {calcListeningScore !== '' && Number(calcListeningScore) >= 33 && (
                                            <span style={{ color: '#047857' }}>🌟 Excellent! Meets CLB 9+ requirement for maximum Express Entry CRS points.</span>
                                        )}
                                        {calcListeningScore !== '' && Number(calcListeningScore) >= 27 && Number(calcListeningScore) < 33 && (
                                            <span style={{ color: '#b45309' }}>👍 Good standing! Meets CLB 7–8 standard PR requirements.</span>
                                        )}
                                        {calcListeningScore !== '' && Number(calcListeningScore) < 27 && (
                                            <span style={{ color: '#800000' }}>⚡ Needs practice. Recommend completing more practice tests.</span>
                                        )}
                                        {calcListeningScore === '' && (
                                            <span>Enter your score above to calculate level.</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Official Score Reference Conversion Table */}
                        <div className="paragon-calc-reference-card">
                            <h3 className="paragon-calc-ref-title">
                                📋 Official CELPIP Score Conversion Table
                            </h3>
                            <p style={{ fontSize: '13.5px', color: '#6b7280', margin: '4px 0 16px' }}>
                                Quick reference guide showing raw score ranges needed for each official Canadian Language Benchmark (CLB) level.
                            </p>

                            <table className="paragon-calc-table">
                                <thead>
                                    <tr>
                                        <th>CELPIP / CLB Level</th>
                                        <th>Reading Score (/38)</th>
                                        <th>Listening Score (/38)</th>
                                        <th>Proficiency Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={{ background: '#f0fdf4' }}>
                                        <td><strong style={{ color: '#047857' }}>10–12</strong></td>
                                        <td>33 – 38</td>
                                        <td>35 – 38</td>
                                        <td>Advanced proficiency in workplace and community contexts</td>
                                    </tr>
                                    <tr style={{ background: '#f0fdf4' }}>
                                        <td><strong style={{ color: '#047857' }}>9</strong></td>
                                        <td>31 – 32</td>
                                        <td>33 – 34</td>
                                        <td>Effective proficiency (Max Express Entry CRS threshold)</td>
                                    </tr>
                                    <tr style={{ background: '#fffbeb' }}>
                                        <td><strong style={{ color: '#b45309' }}>8</strong></td>
                                        <td>28 – 30</td>
                                        <td>30 – 32</td>
                                        <td>Good proficiency in workplace contexts</td>
                                    </tr>
                                    <tr style={{ background: '#fffbeb' }}>
                                        <td><strong style={{ color: '#b45309' }}>7</strong></td>
                                        <td>24 – 27</td>
                                        <td>27 – 29</td>
                                        <td>Adequate proficiency (Standard Federal Skilled Worker minimum)</td>
                                    </tr>
                                    <tr>
                                        <td><strong>6</strong></td>
                                        <td>19 – 23</td>
                                        <td>22 – 26</td>
                                        <td>Developing proficiency in workplace contexts</td>
                                    </tr>
                                    <tr>
                                        <td><strong>5</strong></td>
                                        <td>15 – 18</td>
                                        <td>17 – 21</td>
                                        <td>Acquiring proficiency in workplace contexts</td>
                                    </tr>
                                    <tr>
                                        <td><strong>4</strong></td>
                                        <td>10 – 14</td>
                                        <td>11 – 16</td>
                                        <td>Adequate for daily community interactions</td>
                                    </tr>
                                    <tr>
                                        <td><strong>3</strong></td>
                                        <td>8 – 9</td>
                                        <td>7 – 10</td>
                                        <td>Basic proficiency in community contexts</td>
                                    </tr>
                                    <tr style={{ color: '#9ca3af' }}>
                                        <td><em>M</em></td>
                                        <td>0 – 7</td>
                                        <td>0 – 6</td>
                                        <td>Minimal proficiency (or insufficient assessment information)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* TAB 3: PERFORMANCE ANALYTICS (Full CheckProgress Integration) */}
                {activeTab === 'analytics' && (
                    <div>
                        {/* Section Header with Module Filter Buttons */}
                        <div className="paragon-section-header">
                            <div>
                                <h2 className="paragon-section-title">Performance Analytics &amp; Diagnostics</h2>
                                <p style={{ fontSize: '15px', color: '#6b7280', margin: '6px 0 0' }}>
                                    Comprehensive record of all completed practice tests, task evaluations, CLB levels, and historical attempts.
                                </p>
                            </div>
                            <div className="paragon-filter-group">
                                <button
                                    className={`paragon-filter-btn ${analyticsModuleFilter === 'all' ? 'active' : ''}`}
                                    onClick={() => setAnalyticsModuleFilter('all')}
                                >
                                    All Modules
                                </button>
                                <button
                                    className={`paragon-filter-btn ${analyticsModuleFilter === 'reading' ? 'active' : ''}`}
                                    onClick={() => setAnalyticsModuleFilter('reading')}
                                >
                                    📖 Reading
                                </button>
                                <button
                                    className={`paragon-filter-btn ${analyticsModuleFilter === 'listening' ? 'active' : ''}`}
                                    onClick={() => setAnalyticsModuleFilter('listening')}
                                >
                                    🎧 Listening
                                </button>
                                <button
                                    className={`paragon-filter-btn ${analyticsModuleFilter === 'writing' ? 'active' : ''}`}
                                    onClick={() => setAnalyticsModuleFilter('writing')}
                                >
                                    ✍️ Writing
                                </button>
                                <button
                                    className={`paragon-filter-btn ${analyticsModuleFilter === 'speaking' ? 'active' : ''}`}
                                    onClick={() => setAnalyticsModuleFilter('speaking')}
                                >
                                    🗣️ Speaking
                                </button>
                            </div>
                        </div>

                        {/* Top Performance Analytics Summary Banner */}
                        <div className="paragon-analytics-summary">
                            <div className="paragon-analytics-stat-card">
                                <div className="stat-num" style={{ color: '#800000' }}>
                                    {readingAttempts.length + listeningAttempts.length + writingAttempts.length + speakingAttempts.length}
                                </div>
                                <div className="stat-lbl">Total Attempts Recorded</div>
                            </div>
                            <div className="paragon-analytics-stat-card">
                                <div className="stat-num" style={{ color: '#059669' }}>{readingAttempts.length}</div>
                                <div className="stat-lbl">📖 Reading Attempts</div>
                            </div>
                            <div className="paragon-analytics-stat-card">
                                <div className="stat-num" style={{ color: '#0284c7' }}>{listeningAttempts.length}</div>
                                <div className="stat-lbl">🎧 Listening Attempts</div>
                            </div>
                            <div className="paragon-analytics-stat-card">
                                <div className="stat-num" style={{ color: '#d97706' }}>{writingAttempts.length}</div>
                                <div className="stat-lbl">✍️ Writing Tasks</div>
                            </div>
                            <div className="paragon-analytics-stat-card">
                                <div className="stat-num" style={{ color: '#800000' }}>{speakingAttempts.length}</div>
                                <div className="stat-lbl">🗣️ Speaking Tasks</div>
                            </div>
                        </div>

                        {/* Detailed Module Columns */}
                        {analyticsLoading ? (
                            <div style={{ textAlign: 'center', padding: '60px 0', fontSize: '18px', color: '#6b7280' }}>
                                Loading performance diagnostics and attempt history...
                            </div>
                        ) : (
                            <div className="paragon-analytics-grid">
                                {/* READING COLUMN */}
                                {(analyticsModuleFilter === 'all' || analyticsModuleFilter === 'reading') && (
                                    <div className="paragon-analytics-column">
                                        <div className="paragon-analytics-col-header" style={{ borderLeftColor: '#059669' }}>
                                            <div className="col-header-title">📖 Reading (Test 1–50)</div>
                                            <span className="col-header-count">{readingAttempts.length} Attempted</span>
                                        </div>
                                        <div className="paragon-analytics-scroll-box">
                                            {renderReadingAnalytics()}
                                        </div>
                                    </div>
                                )}

                                {/* LISTENING COLUMN */}
                                {(analyticsModuleFilter === 'all' || analyticsModuleFilter === 'listening') && (
                                    <div className="paragon-analytics-column">
                                        <div className="paragon-analytics-col-header" style={{ borderLeftColor: '#0284c7' }}>
                                            <div className="col-header-title">🎧 Listening (Test 1–50)</div>
                                            <span className="col-header-count">{listeningAttempts.length} Attempted</span>
                                        </div>
                                        <div className="paragon-analytics-scroll-box">
                                            {renderListeningAnalytics()}
                                        </div>
                                    </div>
                                )}

                                {/* WRITING COLUMN */}
                                {(analyticsModuleFilter === 'all' || analyticsModuleFilter === 'writing') && (
                                    <div className="paragon-analytics-column">
                                        <div className="paragon-analytics-col-header" style={{ borderLeftColor: '#d97706' }}>
                                            <div className="col-header-title">✍️ Writing (Test 1–50)</div>
                                            <span className="col-header-count">{writingAttempts.length} Tasks</span>
                                        </div>
                                        <div className="paragon-analytics-scroll-box">
                                            {renderWritingAnalytics()}
                                        </div>
                                    </div>
                                )}

                                {/* SPEAKING COLUMN */}
                                {(analyticsModuleFilter === 'all' || analyticsModuleFilter === 'speaking') && (
                                    <div className="paragon-analytics-column">
                                        <div className="paragon-analytics-col-header" style={{ borderLeftColor: '#800000' }}>
                                            <div className="col-header-title">🗣️ Speaking (Test 1–50)</div>
                                            <span className="col-header-count">{speakingAttempts.length} Tasks</span>
                                        </div>
                                        <div className="paragon-analytics-scroll-box">
                                            {renderSpeakingAnalytics()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <DashboardPurchaseNotification />


            </div>
        </div>
    );
};

// Recent Purchase Notification Toast for Dashboard
const dashboardPurchases = [
  { name: "Gurpreet S.", location: "Brampton, ON", item: "50 Full AI Practice Tests", timeAgo: "2 minutes ago" },
  { name: "Navneet K.", location: "Surrey, BC", item: "CELPIP Self-Paced Course", timeAgo: "5 minutes ago" },
  { name: "Harpreet M.", location: "Calgary, AB", item: "Booked Free Demo Class", timeAgo: "9 minutes ago" },
  { name: "Raman D.", location: "Mississauga, ON", item: "Writing & Speaking Templates", timeAgo: "14 minutes ago" },
  { name: "Simranjit P.", location: "Edmonton, AB", item: "50 Full AI Practice Tests", timeAgo: "18 minutes ago" },
  { name: "Kavita R.", location: "Toronto, ON", item: "CELPIP Master Coaching", timeAgo: "23 minutes ago" },
];

function DashboardPurchaseNotification() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!isDismissed) setIsVisible(true);
    }, 2500);
    return () => clearTimeout(t);
  }, [isDismissed]);

  useEffect(() => {
    if (isDismissed) return;
    let timer;
    if (isVisible) {
      timer = setTimeout(() => setIsVisible(false), 5500);
    } else {
      timer = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % dashboardPurchases.length);
        setIsVisible(true);
      }, 6500);
    }
    return () => clearTimeout(timer);
  }, [isVisible, isDismissed]);

  if (isDismissed || !isVisible) return null;
  const current = dashboardPurchases[currentIndex];

  return (
    <div style={{
      position: "fixed",
      bottom: "20px",
      left: "20px",
      zIndex: 9999,
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      backdropFilter: "blur(10px)",
      color: "#1e293b",
      border: "1px solid #e2e8f0",
      boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
      borderRadius: "16px",
      padding: "14px 18px",
      maxWidth: "340px",
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      fontFamily: "system-ui, -apple-system, sans-serif",
      transition: "all 0.3s ease"
    }}>
      <div style={{
        width: "36px",
        height: "36px",
        borderRadius: "10px",
        backgroundColor: "#ecfdf5",
        color: "#059669",
        border: "1px solid #a7f3d0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: "16px"
      }}>
        <FaShoppingBag />
      </div>
      <div style={{ flex: 1, paddingRight: "16px" }}>
        <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 600 }}>
          <strong style={{ color: "#0f172a" }}>{current.name}</strong> • {current.location}
        </div>
        <div style={{ fontSize: "12px", fontWeight: 700, color: "#1e293b", marginTop: "2px", lineHeight: "1.3" }}>
          Purchased <span style={{ color: "#800000", fontWeight: 800 }}>{current.item}</span>
        </div>
        <div style={{ fontSize: "10px", color: "#94a3b8", display: "flex", alignItems: "center", gap: "6px", marginTop: "4px" }}>
          <span style={{ color: "#059669", fontWeight: 700, display: "flex", alignItems: "center", gap: "3px" }}>
            <FaCheckCircle style={{ fontSize: "10px" }} /> Verified
          </span>
          <span>•</span>
          <span>{current.timeAgo}</span>
        </div>
      </div>
      <button
        onClick={() => { setIsVisible(false); setIsDismissed(true); }}
        style={{
          position: "absolute",
          top: "8px",
          right: "8px",
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          backgroundColor: "#f1f5f9",
          border: "none",
          color: "#64748b",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: "11px"
        }}
        aria-label="Close notification"
      >
        <FaTimes />
      </button>
    </div>
  );
}

export default Dashboard;


